import { useState, useRef, useCallback } from 'react';

const galleryImages = [
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/69d25f0af7e3348edc5ca341a16504b8.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/c1261f3cf6fee59c0ee083f182641dcd.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/aebe87bef4aa2d20062e62f55b500830.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/9600a465f7587d3a0f6f12b160eb77c2.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/10670d014d86903cc13fd619a393ddc9.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/4f24b9649e2f97a560ecc6e2acab1379.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/534c6efec12d2495cab62a4f0c8fe1ad.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/c38b968140ffee695bd510f4df5b0629.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/f8e480c51e1726ff8ae7df370dd5c268.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/3965385c4d739eafff5802fcd0bbabd6.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/0364e0a35a9d2071c14399255e09ac12.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/f35870097f9882694d1e38260fdc8e91.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/713ec1b20ea130aa1ba3892f636862c1.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/635d33cbf5095ca047b73f30e8a3d4fd.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/01967cf3a327f05f64c835435dea63a7.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/849396c95bf771725cf9b463a76fd9dc.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/ba8be52893522ca222ba6625147f6e8c.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/ebf324fad618b31a6616a718687eda09.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/db15377fe6d8efc2c7a01d4592a591a0.png',
  'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/a2859f91c2e8e778e5d19628ef0e771d.png',
];

export function GallerySection() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [visibleImages, setVisibleImages] = useState<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const setImageRef = useCallback((index: number) => (el: HTMLDivElement | null) => {
    imageRefs.current[index] = el;
    
    if (el && !observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const idx = imageRefs.current.indexOf(entry.target as HTMLDivElement);
              if (idx !== -1) {
                setVisibleImages(prev => new Set(prev).add(idx));
              }
            }
          });
        },
        { rootMargin: '50px' }
      );
    }

    if (el && observerRef.current) {
      observerRef.current.observe(el);
    }
  }, []);

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % galleryImages.length);
    }
  };

  const prevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage - 1 + galleryImages.length) % galleryImages.length);
    }
  };

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              ref={setImageRef(index)}
              className="relative aspect-[3/4] overflow-hidden group cursor-pointer bg-gray-100"
              onClick={() => openLightbox(index)}
            >
              {visibleImages.has(index) && (
                <>
                  <img
                    src={image}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
            aria-label="Close"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
            aria-label="Previous"
          >
            <i className="ri-arrow-left-line text-3xl"></i>
          </button>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
            aria-label="Next"
          >
            <i className="ri-arrow-right-line text-3xl"></i>
          </button>

          {/* Image */}
          <div
            className="max-w-6xl max-h-[90vh] w-full h-full flex items-center justify-center p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[selectedImage]}
              alt={`Gallery ${selectedImage + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm">
            {selectedImage + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </section>
  );
}
